// © 2025 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {DatePipe, NgTemplateOutlet} from '@angular/common';

/**
 * How a quick link is opened. `web` is an ordinary `https:` URL (a new browser tab); `app` is a custom-scheme
 * URL (`tg://…`) the operating system hands to the installed messenger.
 *
 * The two are separate values rather than something derived from the URL's scheme on this side: whether an
 * installation prefers the web or the app handle is the SERVER's decision (it knows which handle the employee
 * consented to share), and sniffing `startsWith('http')` here would silently disagree with it the first time a
 * channel ships a third scheme.
 */
export type ContactQuickLinkKind = 'web' | 'app';

/** The address half of a chip: where to go, and how. */
export interface ContactQuickLinkTarget {
  readonly url: string;
  readonly kind: ContactQuickLinkKind;
}

/**
 * What {@link ContactChannelChipComponent} needs to render one channel.
 *
 * Declared HERE, in `_ui`, rather than imported from the company module: a shared control must not depend on
 * the feature that happens to feed it first. The wire model
 * (`_modules/company/contact-quick-links/contact-quick-link.models.ts`) *extends* this interface, so a
 * placement binds the server's object straight in and the compiler holds the two in step.
 */
export interface ContactChipLink {
  /** The channel's wire id (`"telegram"`). Used for the icon and as the fallback display label. */
  readonly channel: string;
  /**
   * The handle as the employee's contact shows it — `@NikoKlemm`, a phone number, a display name.
   *
   * Optional, and that is a wire fact rather than a convenience: the monolith serialises with
   * `NullValueHandling.Ignore`, so a channel with no display name arrives with the property **missing**, not
   * `null`. Every field here therefore treats absent and null alike; a model that only allowed `null` would
   * type-check and then read `undefined` at runtime.
   */
  readonly displayName?: string | null;
  /** Where the chip goes. Absent/`null` when the company holds consent but no reachable address. */
  readonly quickLink?: ContactQuickLinkTarget | null;
  /** When the link was last proven live; drives the tooltip's „bestätigt" line. */
  readonly lastVerifiedUtc?: string | null;
}

/**
 * A manager's one-click way to reach an employee on a channel that employee has consented to share (#558).
 *
 * **Why a chip and not a link.** Managers already saw the link *state* („Telegram: Verknüpft") on three
 * screens, which answers a question nobody has — a manager who is looking at a roster at 22:40 wants to reach
 * the person, not to audit the consent. The chip carries the same fact (the channel is linked) as a
 * side-effect of being the thing you press, so the state wording is replaced rather than accompanied.
 *
 * **Three renderings, one control:**
 *
 * | `quickLink` | Element | Why |
 * | --- | --- | --- |
 * | `kind: 'web'` | `<a target="_blank" rel="noopener">` | an ordinary URL — a new tab, and `noopener` so the opened page cannot reach back through `window.opener` |
 * | `kind: 'app'` | the same anchor, custom scheme | the OS resolves `tg://…`; the tooltip says so out loud, because a link that visibly does nothing in a browser without the app installed is otherwise indistinguishable from a broken one |
 * | `null` | `<span>`, not focusable | there is nothing to open. A disabled-looking anchor that swallows its own click is worse than a plain badge: it takes a tab stop and promises an action it does not have |
 *
 * Sized to fit a table cell (`text-xs`, `py-0.5`, `whitespace-nowrap`) and to sit correctly beside larger text
 * — `align-top` is not cosmetic here: an `inline-flex` box aligns on its own baseline and otherwise hangs
 * ~1.5 px low beside a `text-sm` name, which reads as "the chips sit lower than the names" once a roster
 * stacks up (see the style guide's *Status badge / pill*).
 *
 * **Accessible name.** The icon is `aria-hidden` (it is the same information as the label, in a form a screen
 * reader cannot read) and the channel name is carried by an `sr-only` span, so the anchor announces
 * „Telegram: @NikoKlemm" rather than the bare handle — which, out of the column's context, names nothing.
 */
@Component({
  selector: 'cqme-contact-chip',
  standalone: true,
  // The glyph is declared ONCE as an `ng-template` and rendered by both branches — an icon copied into the
  // anchor and the span is an icon that gets fixed in one of the two places.
  imports: [NgTemplateOutlet],
  templateUrl: './contact-chip.component.html',
  // DatePipe is NOT `providedIn: 'root'`, so it is provided here rather than pushed onto every screen that
  // places a chip — a shared control that makes its callers provide a framework pipe is a control that gets
  // copy-pasted instead of reused. Component scope is enough: the pipe is stateless and reads LOCALE_ID from
  // the app injector, which is where `de-DE` is set.
  providers: [DatePipe],
  // The HOST must be constrainable, or no caller can cap the chip. A custom element is `display: inline` with
  // no width rules; as a flex item of a 96 px table cell it never shrank below its content, the chip's own
  // `max-w-full` resolved against that 158 px host, and the handle painted straight over the next column
  // (seen on the roster's Kontakt cell, first docs capture). `inline-flex` + `max-w-full` + `min-w-0` make
  // the host obey whatever box it is placed in, so the inner `truncate` has something to truncate against.
  host: {class: 'inline-flex max-w-full min-w-0 align-middle'},
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactChannelChipComponent {
  private readonly dates = inject(DatePipe);

  /** The channel to render. Required — a chip with nothing to show must not be created by the caller. */
  readonly link = input.required<ContactChipLink>();

  /**
   * The channel's German display name („Telegram").
   *
   * An input rather than a lookup table in this file, deliberately: the catalogue that maps a wire channel to
   * its German name already exists once
   * (`_modules/company/employees/contact-requirements/contact-channels.ts`, which documents itself as "the ONE
   * place `telegram` maps to `Telegram`"), and a second copy in a shared control is exactly the copy that would
   * still say „Telegram" after somebody renamed it. Omitted, the chip falls back to the capitalised wire id,
   * which is right for every channel whose name IS its id and wrong for none that exist today.
   */
  readonly channelLabel = input<string | undefined>(undefined);

  /** The channel's German name — the caller's, or the capitalised wire id. */
  protected readonly label = computed(() => {
    const explicit = this.channelLabel()?.trim();
    if (explicit) return explicit;
    const channel = this.link().channel;
    return channel.charAt(0).toUpperCase() + channel.slice(1);
  });

  /**
   * The handle, which is what the chip actually shows — falling back to the channel's own name.
   *
   * A chip with no visible text would be a bare icon a pointer could not identify and a screen reader would
   * announce as „Telegram:" and nothing else. „Telegram" alone is less information than „@NikoKlemm", but it
   * is true, and it is a label.
   */
  protected readonly handle = computed(() => this.link().displayName?.trim() || this.label());

  /** `null` when the channel is consented but unreachable — the non-interactive rendering. */
  protected readonly target = computed(() => this.link().quickLink ?? null);

  /**
   * The tooltip.
   *
   * Whole sentences rather than a dot-joined fragment list: this string is also the anchor's accessible
   * *description*, and „Telegram · bestätigt 01.09.2026" is not a sentence anybody hears usefully. The
   * confirmation date is always last, so the lead sentence — which differs per kind, and is the one thing a
   * hovering manager needs — is never pushed off the front.
   */
  protected readonly tooltip = computed(() => {
    const target = this.target();
    const lead = target === null
      ? `${this.label()}: ${this.handle()}. Kein Direktlink verfügbar.`
      : target.kind === 'app'
        ? `Öffnet die ${this.label()}-App.`
        : `${this.label()} öffnen.`;

    const verified = this.verifiedOn();
    return verified ? `${lead} Zuletzt bestätigt ${verified}.` : lead;
  });

  /**
   * `lastVerifiedUtc` as `dd.MM.yyyy`, or `null`.
   *
   * Through the real `DatePipe` (provided by this component) rather than `toLocaleDateString`: the app pins
   * `LOCALE_ID` to `de-DE`, and a hand-rolled format would quietly render the runner's locale in a test and
   * the browser's on a manager's laptop.
   *
   * The `try` is not defensive padding. `DatePipe.transform` **throws** `NG02100` on a value it cannot parse
   * — it does not answer `null` — and this computed is read while rendering a table cell, so one malformed
   * `lastVerifiedUtc` in one row would take down the whole roster's change detection. Dropping the sentence
   * is the right answer to "we do not know when this was confirmed"; „bestätigt Invalid Date" is not.
   */
  private readonly verifiedOn = computed(() => {
    const raw = this.link().lastVerifiedUtc;
    if (!raw) return null;
    try {
      return this.dates.transform(raw, 'dd.MM.yyyy');
    } catch {
      return null;
    }
  });
}
